import { isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, OnInit, PLATFORM_ID, signal, DOCUMENT } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [],
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss'
})
export class LoginPageComponent implements OnInit {
  protected readonly auth = inject(AuthService);
  protected readonly copyFeedback = signal<string | null>(null);
  protected readonly proxyVersion = signal<string | null>(null);
  protected readonly isDevEnvironment = computed(() => {
    const env = (this.auth.settings()?.environmentName ?? '').trim().toLowerCase();
    return ['local', 'devcontainer', 'development'].includes(env);
  });
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly settingsService = inject(SettingsService);

  private get beHost(): string {
    const location = this.document.location;
    if (!location) return '';

    // For deployed environments use the configured base URL from appsettings.
    const configuredBase = this.auth.settings()?.hangfireBaseUrl;
    if (configuredBase) return configuredBase;

    return `http://${location.hostname}:5001`;
  }

  async ngOnInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    await Promise.allSettled([this.auth.initialize(), this.loadProxyVersion()]);
  }

  protected async signIn(): Promise<void> {
    this.copyFeedback.set(null);
    await this.auth.login();
  }

  protected signOut(): void {
    this.copyFeedback.set(null);
    this.auth.logout();
  }

  protected async getRoles(): Promise<void> {
    await this.auth.loadRoles();
  }

  protected async copyAccessToken(): Promise<void> {
    const accessToken = this.auth.accessToken();
    if (!accessToken) {
      this.copyFeedback.set('No access token is available for this session.');
      return;
    }

    try {
      await this.writeToClipboard(accessToken);
      this.copyFeedback.set('Access token copied to the clipboard.');
    } catch {
      this.copyFeedback.set('The browser blocked automatic copy.');
    }
  }

  /** Opens the Hangfire dashboard in a new tab, pre-authenticated using the current access token. */
  protected openHangfireDashboard(): void {
    const accessToken = this.auth.accessToken();
    if (!accessToken) return;

    const form = this.document.createElement('form');
    form.method = 'POST';
    form.action = `${this.beHost}/api/hangfire/auth`;
    form.target = '_blank';

    const input = this.document.createElement('input');
    input.type = 'hidden';
    input.name = 'access_token';
    input.value = accessToken;
    form.appendChild(input);

    this.document.body.appendChild(form);
    form.submit();
    this.document.body.removeChild(form);
  }

  private async writeToClipboard(value: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('Clipboard access requires a browser context.');
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textArea = this.document.createElement('textarea');
    textArea.value = value;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    this.document.body.appendChild(textArea);
    textArea.select();
    textArea.setSelectionRange(0, value.length);

    const copied = this.document.execCommand('copy');
    this.document.body.removeChild(textArea);

    if (!copied) {
      throw new Error('Clipboard copy failed.');
    }
  }

  private async loadProxyVersion(): Promise<void> {
    try {
      const version = await firstValueFrom(this.settingsService.getProxyVersion());
      this.proxyVersion.set(version || null);
    } catch {
      this.proxyVersion.set(null);
    }
  }
}
